import { assetsMap, placePicture, ROOT_ID, type Asset, type Paint, type PictureCrop, type Rect } from '@flobi/artwork-core';
import type * as Y from 'yjs';

/**
 * How big each picture is drawn on the page, so the canvas can load it at that size rather
 * than the whole original (the way Flow serves a node's media by zoom, `lib/media/cdnSize`).
 *
 * The answer is the longest side of the picture's pixels as drawn, in PAGE units: a 6000 px
 * photograph cropped into a 300 × 200 frame is drawn 300 wide, so 300. The canvas multiplies
 * it by the zoom and the screen's pixel ratio to know how many pixels it needs. Each use of
 * a picture counts and the biggest wins. `Infinity` is "only the original will do" — a tiled
 * fill is drawn at the picture's own pixel size, so a smaller copy would draw smaller tiles.
 */
export type PictureSizes = ReadonlyMap<string, number>;

interface SceneLike {
  get(id: string): unknown;
  childrenOf(id: string): readonly string[];
}
type Box = { width: number; height: number } | null | undefined;

/** How far a crop's transform zooms into the picture (1 when it does not). */
function cropZoom(transform: readonly number[] | undefined): number {
  if (!transform) return 1;
  const [a = 1, b = 0, c = 0, d = 1] = transform;
  return Math.max(1, Math.hypot(a, b), Math.hypot(c, d));
}

/** The picture's longest drawn side for a box `w` × `h` on the page. */
function drawnLongest(asset: Asset | undefined, w: number, h: number, fit: string | undefined, crop?: PictureCrop): number {
  const iw = asset?.width, ih = asset?.height;
  if (!(w > 0 && h > 0)) return 0;
  // Its own size unknown: a crop can draw it past the frame, so allow for that.
  if (!iw || !ih) return (Math.max(w, h) * 1.5) / Math.min(1, crop?.width ?? 1, crop?.height ?? 1);
  const whole = placePicture({ width: w, height: h }, { width: iw, height: ih }, fit === 'fit' ? 'contain' : 'cover', crop);
  return Math.max(whole.width, whole.height);
}

/** How big each picture is drawn (`sizes`), and where on the page each is drawn (`boxes`, in page units). */
export interface PicturePlaces {
  readonly sizes: PictureSizes;
  readonly boxes: ReadonlyMap<string, readonly Rect[]>;
}

export function pictureSizes(scene: SceneLike, root: Y.Doc, worldBox: (id: string) => Box): PictureSizes {
  return picturePlaces(scene, root, worldBox).sizes;
}

export function picturePlaces(scene: SceneLike, root: Y.Doc, worldBox: (id: string) => Box): PicturePlaces {
  const assets = assetsMap(root);
  const sizes = new Map<string, number>();
  const boxes = new Map<string, Rect[]>();
  const note = (assetId: string, size: number, box?: Box) => {
    if (size > (sizes.get(assetId) ?? 0)) sizes.set(assetId, size);
    if (box && typeof (box as Rect).x === 'number' && typeof (box as Rect).y === 'number') {
      const list = boxes.get(assetId);
      if (list) list.push(box as Rect);
      else boxes.set(assetId, [box as Rect]);
    }
  };
  const visit = (id: string) => {
    const node = scene.get(id) as {
      type?: string; assetId?: string; fit?: string; crop?: PictureCrop; width?: number; height?: number;
      fills?: Paint[]; strokes?: Paint[]; style?: { fills?: Paint[] };
    } | undefined;
    if (!node) {
      for (const child of scene.childrenOf(id)) visit(child);
      return;
    }
    let box: Box | undefined;
    const world = () => (box === undefined ? (box = worldBox(id)) : box);
    if (node.type === 'image' && node.assetId) {
      const b = world();
      const local = Math.max(node.width ?? 0, node.height ?? 0);
      // The world box holds what a group or a turn did to the frame.
      const grown = b && local > 0 ? Math.max(b.width, b.height) / local : 1;
      const asset = assets.get(node.assetId) as Asset | undefined;
      note(node.assetId, drawnLongest(asset, node.width ?? 0, node.height ?? 0, node.fit, node.crop) * grown, b);
    }
    const paints = [...(node.fills ?? []), ...(node.style?.fills ?? []), ...(node.strokes ?? [])];
    for (const paint of paints) {
      if (paint.type !== 'image') continue;
      if (paint.fit === 'tile') { note(paint.assetId, Infinity, world()); continue; }
      const b = world();
      if (!b) { note(paint.assetId, Infinity); continue; }
      const asset = assets.get(paint.assetId) as Asset | undefined;
      note(paint.assetId, drawnLongest(asset, b.width, b.height, paint.fit, paint.crop) * (paint.fit === 'crop' && !paint.crop ? cropZoom(paint.transform) : 1), b);
    }
    for (const child of scene.childrenOf(id)) visit(child);
  };
  visit(ROOT_ID);
  return { sizes, boxes };
}

/** The sizes the canvas asks the storage CDN for, on the picture's long side. */
export const PICTURE_RUNGS = [512, 1024, 2048] as const;

/**
 * Which copy to load for `pixels` device pixels on the long side: the smallest rung that
 * covers it, or the original (`Infinity`) when nothing smaller covers it, or when the
 * original is barely bigger than that rung anyway (a re-encoded copy of nearly the same
 * size is all loss and no gain).
 */
export function rungFor(pixels: number, originalLongest?: number): number {
  if (!(pixels >= 0) || !Number.isFinite(pixels)) return Infinity;
  const rung = PICTURE_RUNGS.find((size) => size >= pixels);
  if (!rung) return Infinity;
  if (originalLongest && rung >= originalLongest * 0.8) return Infinity;
  return rung;
}
